import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { StatusCode } from '@hungryroot/rest';
import {
  postCancelCustomerAccount,
  type CancelData,
} from '@hungryroot/rest/api/cancel_customer_account/post';
import { useCancelWinback } from './hooks/useGetCancelWinback';
import { Navigation, getNext, getPrev } from './navigation';
import { Confirm } from './pages/Confirm/Confirm';
import { Pause } from './pages/Pause/Pause';
import { PrimaryReason } from './pages/PrimaryReason/PrimaryReason';
import { SecondaryReason } from './pages/SecondaryReason/SecondaryReason';
import { Success } from './pages/Success/Success';
import { Winback } from './pages/Winback/Winback';
import styles from './CancelComponent.module.css';

async function cancelCustomerAccount(data: CancelData) {
  // TODO: may need to add check to confirm cancel_reason exists
  const resp = await postCancelCustomerAccount(data);
  if (resp.status !== StatusCode.SuccessOK) {
    throw new Error(JSON.stringify(resp.data, null, 2));
  }

  return resp;
}

interface Props {
  testid?: string;
  className?: string;
}

export interface CancelState {
  cancelReasonId: number | null;
  cancelReasonText: string | null;
  cancelSubreasonId: number[];
  cancelFreeText: string | null;
  cancelFreeTextHeader: string | null;
  next: Navigation | null;
  prev: Navigation | null;
}

const defaultCancelState = {
  cancelReasonId: null,
  cancelReasonText: null,
  cancelSubreasonId: [],
  cancelFreeText: null,
  cancelFreeTextHeader: null,
  next: null,
  prev: null,
};

async function cancel(cancelState: CancelState) {
  if (!cancelState.cancelReasonId) {
    return;
  }

  const data = {
    cancel_reason: cancelState.cancelReasonId,
    cancel_subreason: cancelState.cancelSubreasonId
      ? cancelState.cancelSubreasonId
      : [],
    free_text_response: cancelState.cancelFreeText,
  };

  const res = await cancelCustomerAccount(data);

  if (res.status !== StatusCode.SuccessOK) {
    return;
  }
}

export function Cancel(props: Props) {
  const { testid, className } = props;
  const [cancelState, setCancelState] =
    useState<CancelState>(defaultCancelState);
  const history = useHistory();
  const {
    location: { pathname },
  } = history;

  const cancelWinbackQuery = useCancelWinback();
  const { data: cancelWinbackPromoData } = cancelWinbackQuery || {};
  const showWinback =
    cancelWinbackPromoData?.promo_code !== null &&
    cancelWinbackPromoData?.promo_code !== undefined;

  useEffect(() => {
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [pathname]);

  async function onNext(
    value?:
      | string
      | {
          cancelReasonId: number[];
          cancelReasonText: string | null;
          cancelFreeTextHeader: string | null;
        }
  ) {
    const nextCancelState = getNext(value, pathname, showWinback, cancelState);
    const { next: nextPage } = nextCancelState;

    if (nextPage === null) {
      return;
    }
    if (pathname === Navigation.Confirm && cancelState.cancelReasonId) {
      await cancel(nextCancelState);
    }
    setCancelState(nextCancelState);
    history.push(nextPage);
  }

  function onPrev() {
    const prev = getPrev(pathname, showWinback);
    if (prev === null) {
      return;
    }
    history.push(prev);
  }

  return (
    <section data-testid={testid} className={clsx(styles.container, className)}>
      <div className={styles['container-inner']}>
        <Switch>
          <Route path={Navigation.Pause}>
            <Pause testid={`${testid}-pause`} onNext={onNext} />
          </Route>
          <Route path={Navigation.PrimaryReason}>
            <PrimaryReason
              testid={`${testid}-primary-reason`}
              onNext={onNext}
              onPrev={onPrev}
            />
          </Route>
          <Route path={Navigation.Winback}>
            <Winback
              testid={`${testid}-winback`}
              onNext={onNext}
              onPrev={onPrev}
              promo={cancelWinbackPromoData}
            />
          </Route>
          <Route path={Navigation.SecondaryReason}>
            {!cancelState.cancelReasonId || !cancelState.cancelReasonText ? (
              <Redirect to={Navigation.PrimaryReason} />
            ) : (
              <SecondaryReason
                testid={`${testid}-secondary-reason`}
                onNext={onNext}
                onPrev={onPrev}
                cancelReasonId={cancelState.cancelReasonId}
              />
            )}
          </Route>
          <Route path={Navigation.Confirm}>
            <Confirm
              testid={`${testid}-confirm`}
              cancelFreeTextHeader={cancelState.cancelFreeTextHeader}
              onNext={onNext}
              onPrev={onPrev}
            />
          </Route>
          <Route path={Navigation.Success}>
            <Success testid={`${testid}-success`} />
          </Route>
        </Switch>
      </div>
    </section>
  );
}
